import { AvatarProps, Divider, Stack } from '@mui/material';
import { Box } from '@mui/system';
import React, { PropsWithChildren, useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { Outlet, useNavigate, useParams, useLocation } from 'react-router';
import { ChatThread, IChatThreadProps } from '../../components/chat/ChatThread';
import { ChatThreadMessageItem } from '../../components/chat/ChatThreadMessageItem';

import { ChatChannelContext, ChatChannelContextProvider } from '../../context/chat';
import { UserContext } from '../../context/UserInformation';
import { useTypingMembers } from '../../hooks/chat/useTypingMembers';
import { ChatMessage } from '../../types/db/chat';
import { ERole, getUIUserRoleFrom } from '../../utilities/EnumUtility';
import { ROUTE_PARAMS, ROUTE_PATH, ROUTE_SUBPATH } from '../../utilities/navigation';
import { useTranslation } from 'react-i18next';
import { useUserCurrentChannel } from '../../hooks/chat/useUserCurrentChannel';
import { useChatMessages } from '../../hooks/chat/useChatMessages';

/** The conversation within a thread */
export const ChatContentPage = () => {
    const navigate = useNavigate();
    const { [ROUTE_PARAMS.threadID]: threadID } = useParams();
    const location = useLocation();

    if (!threadID) {
        return null;
    }

    const navigateToChatDetails = () => {
        navigate(ROUTE_PATH.inboxBuild(threadID, true))
    }
    const navigateToInbox = () => {
        navigate(ROUTE_PATH.inboxBuild());
    }

    //to know if the details should be open or not
    const hasDetails = location.pathname.includes(ROUTE_SUBPATH.details);

    return (
        <ChatContentContainer detailsOpened={hasDetails}
            threadId={threadID}
            navigateToDetails={() => navigateToChatDetails()}
            closeThread={() => navigateToInbox()}>
            <Outlet />
        </ChatContentContainer>
    );
}

interface IChatContentContainerProps {
    threadId: string;
    detailsOpened: boolean;
    navigateToDetails: () => void;
    closeThread: () => void;
}

const ChatContentContainer = ({ threadId, detailsOpened, children, navigateToDetails, closeThread }: PropsWithChildren<IChatContentContainerProps>) => {
    const userContext = useContext(UserContext);

    if (!userContext) {
        throw new Error("Need UserContext");
    }

    if (userContext.loading) {
        return null;
    }

    const userId = userContext.user?.firebaseUser?.uid;
    if (!userId) {
        console.warn('ChatContentContainer: No user id');
        return null;
    }

    return (
        <ChatChannelContextProvider channelId={threadId}>
            {
                <Stack direction={'row'}>
                    {/* Don't show side channels list for small screen */}
                    <Box flex={2} display={{ xs: !detailsOpened ? 'block' : 'none', sm: 'block' }}>
                        <ChatThreadContainer userId={userId}
                            threadId={threadId}
                            navigateToDetails={!detailsOpened ? navigateToDetails : undefined}
                            closeThread={closeThread} />
                    </Box>
                    <Divider flexItem orientation='vertical' />
                    <Box flex={1} display={!detailsOpened ? 'none' : 'block'}>
                        {children}
                    </Box>
                </Stack>
            }
        </ChatChannelContextProvider>
    );
}

interface IChatThreadContainerProps
    extends Pick<IChatContentContainerProps, 'threadId' | 'closeThread'>,
    Partial<Pick<IChatContentContainerProps, 'navigateToDetails'>> {
    userId: string;
}
const ChatThreadContainer = ({ userId, threadId: channelId, navigateToDetails, closeThread }: IChatThreadContainerProps) => {
    const chatDetailsContext = useContext(ChatChannelContext);
    if (!chatDetailsContext) {
        throw new Error("Need ChatChannelContext");
    }
    const { t } = useTranslation();
    const { channel: userChannel, markAsRead, saveDraftMessage } = useUserCurrentChannel(userId, channelId);
    const { messages, isLoading: isMessagesLoading, sendMessage } = useChatMessages(channelId);
    const { typingMembers, setCurrentUserTyping } = useTypingMembers(channelId);
    const { members, channel } = chatDetailsContext;
    //the reference to the current draft being typed. if null draft has not changed
    const draft = useRef<string | null>(null);

    useEffect(() => {
        // console.log('mark as read')
        markAsRead();// mark the thread as read since user seeing the thread
    }, [markAsRead])

    useEffect(() => {
        //save the draft on unmount
        return () => {
            // console.log('draft while unmounting', draft.current);
            //if we change channel, we save the draft
            draft.current !== null && saveDraftMessage(draft.current);
            draft.current = null;//reset
        }
    }, [channelId, saveDraftMessage, userId]);

    const renderMessageItem = useCallback((message: (ChatMessage & { id: string })) =>
        <ChatThreadMessageItem
            {...{
                key: message.id,
                displayName: members[message.sender]?.displayName ?? '',
                timestamp: new Date(+message.timestamp),
                message: message.message,
                avatar: {
                    alt: members[message.sender]?.displayName || '',
                    src: members[message.sender]?.avatar || ''
                },
                isSelf: message.sender === userId,
                userRole: getUIUserRoleFrom(members[message.sender]?.role),
            }}
        />, [members, userId])

    //use Memo for loop through members mapping
    const memberItems = useMemo(() => {
        return Object.values(members)
            .reduce((prev, cur) => {
                return ({
                    names: [...prev.names, cur.displayName],
                    avatars: [...prev.avatars,
                    {
                        alt: cur.displayName,
                        src: cur.avatar,
                        role: getUIUserRoleFrom(cur.role) || undefined,
                    }]
                })
            },
                {
                    names: [] as string[],
                    avatars: [] as (AvatarProps & { role?: ERole })[],
                }
            )
    }
        , [members]);

    //filter out current user and map to actual name
    const typingMembersName = useMemo(() =>
        typingMembers.filter(item => item !== userId).map(item => members[item]?.displayName)
        , [members, typingMembers, userId])

    const handleInputChange = useCallback((e) => {
        const inputValue = e.target.value;
        draft.current = inputValue;
        setCurrentUserTyping(inputValue);
    }, [setCurrentUserTyping]);

    const handleSendMessage = useCallback(async (value: string) => {
        await sendMessage(userId, value);
        //crean up the draft after the message has been sent successfully
        draft.current = null
        await saveDraftMessage('');
    }, [saveDraftMessage, sendMessage, userId])

    const props: IChatThreadProps = {
        chatHeader: {
            title: channel?.type === 'direct' ?
                t('chat.thread.directTitle', { names: memberItems.names, defaultValue: "{{names, list}}" })
                : channel?.title,
            members: memberItems.avatars,
            onDetailsClick: navigateToDetails,
            onBackClick: closeThread,
        },
        chatThread: isMessagesLoading ? true : {
            items: messages,
            renderItem: renderMessageItem,
        },
        chatInput: {
            value: userChannel?.draft?.message || '',
            onChange: handleInputChange,
            onSendClick: handleSendMessage,
            typingMembers: typingMembersName,
        },
    }

    return <ChatThread {...props} />;
}
